import React, { useState } from 'react';
import { Link } from 'react-router';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPalette, faTrophy, faComments, faCrown, faLink, faShareNodes } from '@fortawesome/free-solid-svg-icons';
import MagicButton from 'components/MagicButton/MagicButton';
import AvatarWithFlag from 'components/AvatarWithFlag/AvatarWithFlag';
import BadgeIcon from 'components/BadgeIcon/BadgeIcon';
import { AssetUrls } from 'config/assets';
import { toast } from 'react-toastify';
import {
  useGetBillingQuery,
  useCreateCheckoutSessionMutation,
  useReactivateSubscriptionMutation,
} from 'store/services/subscription/subscriptionApi';
import { useGetMeQuery, meApi } from 'store/services/user/meApi';
import CancelSubscriptionModal from '../CancelSubscriptionModal/CancelSubscriptionModal';
import PaymentMethodsSection from './PaymentMethodsSection';
import InvoiceHistorySection from './InvoiceHistorySection';
import { getTmRefCookie } from 'utils/referralCookies';
import './BillingTab.css';

const BillingTab: React.FC = () => {
  const dispatch = useDispatch();
  const { data: userData, isLoading: isUserLoading } = useGetMeQuery();
  const isElite = userData?.isElite || false;
  const { data: billing, isLoading: isBillingLoading, isError: isBillingError, refetch: refetchBilling } = useGetBillingQuery(undefined, { skip: !isElite });
  const [createCheckout, { isLoading: isCheckoutLoading }] = useCreateCheckoutSessionMutation();
  const [reactivate, { isLoading: isReactivating }] = useReactivateSubscriptionMutation();
  const [showCancelModal, setShowCancelModal] = useState(false);

  const isAnnual = billing?.planInterval === 'year';
  const planAmountCents = billing?.planAmount ?? 599;
  const monthlyPriceDisplay = isAnnual
    ? `$${(planAmountCents / 12 / 100).toFixed(2)}/month`
    : `$${(planAmountCents / 100).toFixed(2)}/month`;
  const annualBilledLine = isAnnual
    ? `$${(planAmountCents / 100).toFixed(2)} billed annually`
    : null;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatPaymentMethod = (pm: { brand: string; last4: string } | null) => {
    if (!pm) return 'No payment method on file';
    const brand = pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1);
    return `${brand} ending in ${pm.last4}`;
  };

  const handleSubscribe = async () => {
    try {
      const result = await createCheckout({
        source: 'billing-tab',
        billingInterval: 'annual',
        // Attribute an active referral window even when checkout starts here.
        ref: getTmRefCookie() ?? undefined,
      }).unwrap();
      window.location.href = result.url;
    } catch {
      toast.error('Failed to start checkout. Please try again.');
    }
  };

  const handleReactivate = async () => {
    try {
      await reactivate().unwrap();
      dispatch(meApi.util.invalidateTags(['User']));
      toast.success('Subscription reactivated! Welcome back to Elite.');
    } catch {
      toast.error('Failed to reactivate subscription. Please try again.');
    }
  };

  if (isUserLoading || (isElite && isBillingLoading)) {
    return (
      <div className="billing-loading">
        Loading billing information...
      </div>
    );
  }

  // Non-Elite state
  if (!isElite) {
    return (
      <div className="billing-upsell-card">
        <div className="billing-upsell-header">
          <h3 className="billing-upsell-title">TradeMagic Elite <img src={AssetUrls.icons.elite.badge} alt="Elite" className="billing-upsell-elite-badge" /></h3>
        </div>

        <div className="billing-upsell-preview">
          <AvatarWithFlag
            username={userData?.username}
            avatarUrl={userData?.avatarUrl}
            size={64}
            isElite={true}
            showAnimatedBorder={true}
          />
          <span className="billing-upsell-preview-label">Your avatar with Elite</span>
        </div>

        <ul className="billing-upsell-features">
          <li className="billing-upsell-feature">
            <span className="billing-upsell-feature-icon">
              <FontAwesomeIcon icon={faPalette} />
            </span>
            <div className="billing-upsell-feature-text">
              <span className="billing-upsell-feature-name">Animated Avatar Border</span>
              <span className="billing-upsell-feature-desc">A glowing animated border around your avatar</span>
            </div>
          </li>
          <li className="billing-upsell-feature">
            <span className="billing-upsell-feature-icon">
              <FontAwesomeIcon icon={faTrophy} />
            </span>
            <div className="billing-upsell-feature-text">
              <span className="billing-upsell-feature-name">Expanded Badge Display</span>
              <span className="billing-upsell-feature-desc">Show up to 4 badges on your profile</span>
              <div className="billing-upsell-badge-row">
                <BadgeIcon icon="trophy" shape="shield" tier="gold" size={24} />
                <BadgeIcon icon="star" shape="circle" tier="silver" size={24} />
                <BadgeIcon icon="fire" shape="hex" tier="diamond" size={24} />
                <BadgeIcon icon="crown" shape="shield" tier="mythic" size={24} />
              </div>
            </div>
          </li>
          <li className="billing-upsell-feature">
            <span className="billing-upsell-feature-icon">
              <FontAwesomeIcon icon={faCrown} />
            </span>
            <div className="billing-upsell-feature-text">
              <span className="billing-upsell-feature-name">Elite Profile Badge</span>
              <span className="billing-upsell-feature-desc">Exclusive badge displayed in trades and on your profile</span>
            </div>
          </li>
          <li className="billing-upsell-feature">
            <span className="billing-upsell-feature-icon">
              <FontAwesomeIcon icon={faComments} />
            </span>
            <div className="billing-upsell-feature-text">
              <span className="billing-upsell-feature-name">Chat Customization</span>
              <span className="billing-upsell-feature-desc">Custom gradient bubble colors and community emoji</span>
            </div>
          </li>
          <li className="billing-upsell-feature">
            <span className="billing-upsell-feature-icon">
              <FontAwesomeIcon icon={faLink} />
            </span>
            <div className="billing-upsell-feature-text">
              <span className="billing-upsell-feature-name">Custom Profile URL</span>
              <span className="billing-upsell-feature-desc">Claim a short, memorable profile link</span>
            </div>
          </li>
          <li className="billing-upsell-feature">
            <span className="billing-upsell-feature-icon">
              <FontAwesomeIcon icon={faShareNodes} />
            </span>
            <div className="billing-upsell-feature-text">
              <span className="billing-upsell-feature-name">Social Profile Links</span>
              <span className="billing-upsell-feature-desc">Add platform links to your profile card</span>
            </div>
          </li>
        </ul>

        <div className="billing-upsell-price">
          <span className="billing-price-amount">$3.99</span>
          <span className="billing-price-interval">/month</span>
        </div>
        <p className="billing-upsell-price-billed">$47.88 billed annually</p>
        <MagicButton
          label={isCheckoutLoading ? 'Loading...' : 'Subscribe'}
          color="purple"
          onPress={handleSubscribe}
          isLoading={isCheckoutLoading}
          isDisabled={isCheckoutLoading}
          fullWidth
        />
        <Link to="/elite" className="billing-learn-more">
          See all Elite features &rarr;
        </Link>
      </div>
    );
  }

  // Elite user but billing data failed to load
  if (isBillingError || !billing) {
    return (
      <div className="billing-sections">
        <div className="billing-card">
          <p className="billing-error-text">Failed to load billing information. Please try again.</p>
          <MagicButton
            label="Retry"
            color="purple"
            onPress={refetchBilling}
            fullWidth
          />
        </div>
      </div>
    );
  }

  // Past Due state
  if (billing.status === 'past_due') {
    return (
      <div className="billing-sections">
        <div className="billing-card">
          <div className="billing-plan-header">
            <img
              src={AssetUrls.icons.elite.badge}
              alt="Elite Badge"
              className="billing-elite-badge"
            />
            <div>
              <h3 className="billing-plan-title">TradeMagic Elite</h3>
              <span className="billing-status-badge past-due">Past Due</span>
            </div>
          </div>

          <div className="billing-warning-box">
            Your payment method failed. Please update your payment information below to keep your Elite access.
          </div>

          <div className="billing-detail-rows">
            {billing.paymentMethod && (
              <div className="billing-detail-row">
                <span className="billing-detail-label">Payment method</span>
                <span className="billing-detail-value">{formatPaymentMethod(billing.paymentMethod)}</span>
              </div>
            )}
          </div>
        </div>

        <PaymentMethodsSection hasActiveSubscription={true} />
        <InvoiceHistorySection />
      </div>
    );
  }

  // Cancelling state
  if (billing.status === 'cancelling') {
    return (
      <div className="billing-sections">
        <div className="billing-card">
          <div className="billing-plan-header">
            <img
              src={AssetUrls.icons.elite.badge}
              alt="Elite Badge"
              className="billing-elite-badge"
            />
            <div>
              <h3 className="billing-plan-title">TradeMagic Elite</h3>
              <span className="billing-status-badge cancelling">Cancelling</span>
            </div>
          </div>

          <p className="billing-cancelling-notice">
            Your subscription will end on <strong>{formatDate(billing.currentPeriodEnd)}</strong>.
          </p>

          <div className="billing-detail-rows">
            <div className="billing-detail-row">
              <span className="billing-detail-label">Access until</span>
              <span className="billing-detail-value">{formatDate(billing.currentPeriodEnd)}</span>
            </div>
            <div className="billing-detail-row">
              <span className="billing-detail-label">Plan</span>
              <div className="billing-detail-value">
                <div>{monthlyPriceDisplay}</div>
                {annualBilledLine && (
                  <div className="billing-detail-value-sub">{annualBilledLine}</div>
                )}
              </div>
            </div>
            {billing.paymentMethod && (
              <div className="billing-detail-row">
                <span className="billing-detail-label">Payment method</span>
                <span className="billing-detail-value">{formatPaymentMethod(billing.paymentMethod)}</span>
              </div>
            )}
          </div>

          <div className="billing-resubscribe-section">
            <span className="billing-resubscribe-prompt">Changed your mind?</span>
            <MagicButton
              label={isReactivating ? 'Reactivating...' : 'Resubscribe'}
              color="purple"
              onPress={handleReactivate}
              isLoading={isReactivating}
              isDisabled={isReactivating}
              fullWidth
            />
          </div>
        </div>

        <PaymentMethodsSection hasActiveSubscription={true} />
        <InvoiceHistorySection />
      </div>
    );
  }

  // Active Elite state
  return (
    <div className="billing-sections">
      <div className="billing-card">
        <div className="billing-plan-header">
          <img
            src={AssetUrls.icons.elite.badge}
            alt="Elite Badge"
            className="billing-elite-badge"
          />
          <div>
            <h3 className="billing-plan-title">TradeMagic Elite</h3>
            <span className="billing-status-badge active">Active</span>
          </div>
        </div>

        <div className="billing-detail-rows">
          {billing.eliteSince && (
            <div className="billing-detail-row">
              <span className="billing-detail-label">Elite since</span>
              <span className="billing-detail-value">{formatDate(billing.eliteSince)}</span>
            </div>
          )}
          {billing.currentPeriodEnd && (
            <div className="billing-detail-row">
              <span className="billing-detail-label">Next renewal</span>
              <span className="billing-detail-value">{formatDate(billing.currentPeriodEnd)}</span>
            </div>
          )}
          <div className="billing-detail-row">
            <span className="billing-detail-label">Plan</span>
            <span className="billing-detail-value">$5.99/month</span>
          </div>
          {billing.paymentMethod && (
            <div className="billing-detail-row">
              <span className="billing-detail-label">Payment method</span>
              <span className="billing-detail-value">{formatPaymentMethod(billing.paymentMethod)}</span>
            </div>
          )}
        </div>

        <button
          className="billing-cancel-link"
          onClick={() => setShowCancelModal(true)}
        >
          Cancel subscription
        </button>
      </div>

      <PaymentMethodsSection hasActiveSubscription={true} />
      <InvoiceHistorySection />

      <CancelSubscriptionModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        currentPeriodEnd={billing.currentPeriodEnd}
      />
    </div>
  );
};

export default BillingTab;
